/**
 * suggestionMention — make a suggested message actually reach Prest0n.
 *
 * Owner directive (Miles 2026-08-09, group-thread screenshot): "the suggested
 * messages towards Prest0n should always have @prest0n in them".
 *
 * WHY THIS IS A CORRECTNESS FIX, NOT POLISH. In a thread with two or more
 * humans and the mention requirement on — the DEFAULT for every thread — a
 * suggestion without an @Prest0n mention is a message to the GROUP, and
 * Prest0n stays silent. The user clicked a chip the product offered and got
 * nothing back.
 *
 * CORRECTION (2026-08-26). This paragraph used to claim that starter chips run
 * "the same summon gate as a typed message" because they go through
 * sendProgrammaticMessage. They do go through it, but that path has never
 * consulted the gate — the gate lives in handleSendWithDraftHydration, on the
 * typed-send path only. The claim was wrong, and it mattered: it read as proof
 * that chips were already safe, which is exactly the reasoning that would stop
 * someone from checking.
 *
 * Chips are safe now for a different and better reason — the rule is enforced
 * SERVER-side at the turn boundary
 * (functions/src/prest0nSession/summonGate.ts), so a chip that reaches
 * prest0nRunTurn without a mention gets no reply whichever client path sent
 * it. This module still matters: it is what makes the chip's INTENT visible,
 * so the user sees the mention they are about to send rather than clicking
 * into silence.
 *
 * The mention cannot be baked in server-side when the suggestions are
 * generated: whether a mention is required is a property of the THREAD (human
 * participant count + the per-thread requireMention setting), not the account.
 * The same suggestion must read "Draft an NDA…" in a 1:1 and "@Prest0n Draft an
 * NDA…" in a group thread. So the prefix is applied at RENDER time, against the
 * live gate state, and the chip label shows exactly what will be sent.
 *
 * Pure and dependency-light (imports only the mention predicate) so the rule is
 * unit-tested without React or Firestore.
 */
import { mentionsAssistant } from './summonGate';

/**
 * The canonical handle to prefix with. `summonGate.mentionsAssistant` accepts
 * `@prest0n` case-insensitively (plus preston/-ai aliases); this is the one
 * spelling we WRITE — capital-P + zero, per the brand rule that the chat
 * surface never shows the lowercase spelling or an `s-t-o-n` ending.
 */
export const ASSISTANT_MENTION = '@Prest0n';

/**
 * Does this thread require an explicit mention for a suggestion to be answered?
 * Mirrors the composer placeholder's condition (GlobalChatSystem) so the chips
 * and the placeholder can never disagree about the thread's mode.
 */
export function suggestionsNeedMention(args: {
  gatesThisThread: boolean;
  requireMention: boolean | null | undefined;
}): boolean {
  return Boolean(args.gatesThisThread) && args.requireMention !== false;
}

/**
 * Prefix one suggestion with `@Prest0n` when the thread requires it.
 *
 * Idempotent: a suggestion that already addresses the assistant (in any
 * accepted spelling, anywhere in the text) is returned untouched — no
 * "@Prest0n @Prest0n …". Empty/blank input is returned as-is so a bad
 * suggestion can never become a bare mention, which the composer refuses as a
 * contentless summons (isAssistantMentionOnly).
 */
export function applyAssistantMention(text: string, mentionRequired: boolean): string {
  const value = typeof text === 'string' ? text : '';
  const trimmed = value.trim();
  if (!mentionRequired || !trimmed) return value;
  if (mentionsAssistant(trimmed)) return value;
  return `${ASSISTANT_MENTION} ${trimmed}`;
}

/** Map applyAssistantMention across a suggestion list, dropping blanks. */
export function applyAssistantMentionToAll(
  texts: ReadonlyArray<string> | undefined,
  mentionRequired: boolean,
): string[] {
  if (!Array.isArray(texts)) return [];
  return texts
    .filter((t): t is string => typeof t === 'string' && t.trim().length > 0)
    .map((t) => applyAssistantMention(t, mentionRequired));
}

/**
 * Resolve the chip list for an empty thread, in priority order:
 *
 *   1. `personalized` — the account's memory/document-grounded suggestions
 *      (server-distilled, refreshed by refreshGreetingSuggestions). These are
 *      the ones tailored to the user's actual documents and situation, so they
 *      win whenever they exist.
 *   2. `persisted` — starters stored ON the greeting message, which keeps every
 *      tab showing the same chips for the same thread (multi-tab parity).
 *   3. `configured` — onboarding-written / in-code fallback starters.
 *
 * The winning list is then mention-prefixed for this thread and capped.
 * Returns [] only when every source is empty.
 */
export function resolveSuggestionChips(args: {
  personalized?: ReadonlyArray<string> | undefined;
  persisted?: ReadonlyArray<string> | undefined;
  configured?: ReadonlyArray<string> | undefined;
  mentionRequired: boolean;
  max?: number;
}): string[] {
  const pick = (list: ReadonlyArray<string> | undefined): string[] =>
    Array.isArray(list) ? list.filter((t) => typeof t === 'string' && t.trim().length > 0) : [];
  const chosen = [pick(args.personalized), pick(args.persisted), pick(args.configured)]
    .find((list) => list.length > 0) || [];
  const capped = chosen.slice(0, Math.max(1, args.max ?? 6));
  return applyAssistantMentionToAll(capped, args.mentionRequired);
}
